import { Redirect, withRouter } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import 'react-dates/initialize'
// For React-Redux-Toastr - had to include a css file via CDN as a link tag
// Global CSS Libraries
import 'react-dates/lib/css/_datepicker.css'
import './index.css'
import { Helmet } from 'react-helmet'

import { initializeScripts, SCRIPTS as TRACKER_SCRIPTS } from '../tracking/trackingScripts' // eslint-disable-line
import { LoadingShim } from './common/LoadingShim'
import AuthenticatedAppFallback from './Auth/AuthenticatedAppFallback'
import SessionLoadError from './SessionLoadError'
import { InsightsBootstrapFallback } from './common/InsightsView/InsightsBootstrapFallback'
import useRouteActivityLogger from '../hooks/useRouteActivityLogger'
import useCurrentUser from '../hooks/useCurrentUser'
import useSessionBootstrap from '../hooks/useSessionBootstrap'
import { establishCurrentUser } from '../lib/currentUserSession'
import {
  authenticatedRouteAllowsMobile,
  CHROME_COMPANION_INSIGHTS_PATH,
  getChromeCompanionPath,
  isAuthFreePathname,
  isChromeCompanionInsightsPathname,
  isChromeCompanionRequest,
  isSessionEntryPathname,
  resolveAuthenticatedRoute,
} from '../routing/resolveAuthenticatedRoute'
import {
  CHROME_COMPANION_PROTOCOL_VERSION,
  CHROME_COMPANION_READY_MESSAGE_TYPE,
  getChromeCompanionContextFromMessage,
  isChromeExtensionOrigin,
} from '../routing/chromeCompanionProtocol'
import { ChromeCompanionContext } from '../contexts/ChromeCompanionContext'
import ChromeCompanionSetup from './ChromeCompanionSetup'
import useChromePageContextBridge from '../contexts/useChromePageContextBridge'
import { canAccessInsights, isInsightsPathname } from '../routing/insightsRouteState'
import { InsightsRoutePreloader } from '../routing/InsightsRouteModule'
import { loadCommunityPortfolioOptions } from './common/InsightsView/communityPortfolioDirectory'
import { endInsightsInitialNavigation } from '../performance/insightsPerformance'
import {
  SITE_APP_TITLE,
  SITE_CANONICAL_URL,
  SITE_DESCRIPTION,
  SITE_OG_TYPE,
  SITE_ROBOTS,
  SITE_SEO_TITLE,
  SITE_TWITTER_CARD,
} from '../siteMetadata.mjs'

const AuthHighComponent = lazy(() => import(/* webpackChunkName: "AuthHighComponent" */ './Auth'))
const Onboarding = lazy(() => import(/* webpackChunkName: "Onboarding" */ './Onboarding'))
const PasswordReset = lazy(() => import(/* webpackChunkName: "PasswordReset" */ './PasswordReset'))
const BrandHub = lazy(() => import(/* webpackChunkName: "BrandHub" */ './BrandHub'))
const Changelog = lazy(() => import(/* webpackChunkName: "Changelog" */ './Changelog'))
const PublicPropertyMapPage = lazy(() => import('./common/PropertyMap/PublicPropertyMapPage'))

// Removed Fluent UI global icon initialization (migrating to ShadCN/Tailwind)
const isProduction = import.meta.env.MODE !== 'development'

function DocumentHead({ accountName, loggedIn }) {
  const browserTitle = loggedIn
    ? `${SITE_APP_TITLE}${accountName ? ` (${accountName})` : ''}${isProduction ? '' : ` (${import.meta.env.MODE})`}`
    : SITE_SEO_TITLE

  return (
    <Helmet>
      <title>{browserTitle}</title>
      <meta name="description" content={SITE_DESCRIPTION} />
      <meta name="robots" content={SITE_ROBOTS} />
      <meta name="googlebot" content={SITE_ROBOTS} />
      <link rel="canonical" href={SITE_CANONICAL_URL} />
      <meta property="og:type" content={SITE_OG_TYPE} />
      <meta property="og:title" content={SITE_SEO_TITLE} />
      <meta property="og:description" content={SITE_DESCRIPTION} />
      <meta property="og:url" content={SITE_CANONICAL_URL} />
      <meta name="twitter:card" content={SITE_TWITTER_CARD} />
      <meta name="twitter:title" content={SITE_SEO_TITLE} />
      <meta name="twitter:description" content={SITE_DESCRIPTION} />
      {isProduction
        ? Object.values(TRACKER_SCRIPTS).map((scr, idx) => (
            <script key={idx} type="text/javascript">
              {scr}
            </script>
          ))
        : null}
    </Helmet>
  )
}

const SessionBootstrapFallback = () => (
  <div
    aria-label="Loading ResiDesk"
    className="min-h-screen w-full bg-background"
  />
)

const shouldPreloadInsights = ({ pathname, search, user }) => {
  if (!user?.id) return false
  if (!canAccessInsights(user)) return false
  if (isInsightsPathname(pathname)) return true
  if (!isChromeCompanionRequest({ pathname, search })) return false
  return getChromeCompanionPath(user) === CHROME_COMPANION_INSIGHTS_PATH
}

const useChromeCompanionBootstrap = ({ isChromeCompanionRoute, location }) => {
  const isFramed = window.parent !== window
  const companionSearchRef = useRef(isFramed && isChromeCompanionRoute ? location.search : '')
  const [companionContext, setCompanionContext] = useState(null)
  const pageContext = useChromePageContextBridge(isChromeCompanionRoute && isFramed)

  if (isFramed && isChromeCompanionRoute) companionSearchRef.current = location.search

  useEffect(() => {
    if (!isChromeCompanionRoute || !isFramed) return undefined

    const handleCompanionContext = (event) => {
      if (event.source !== window.parent || !isChromeExtensionOrigin(event.origin)) return

      const nextContext = getChromeCompanionContextFromMessage(event.data)
      if (nextContext) setCompanionContext(nextContext)
    }

    window.addEventListener('message', handleCompanionContext)

    window.parent.postMessage({
      type: CHROME_COMPANION_READY_MESSAGE_TYPE,
      version: CHROME_COMPANION_PROTOCOL_VERSION,
    }, '*')

    return () => window.removeEventListener('message', handleCompanionContext)
  }, [isChromeCompanionRoute, isFramed])

  return {
    companionContext: isChromeCompanionRoute && isFramed ? { ...companionContext, pageContext } : null,
    companionSearch: companionSearchRef.current,
    isFramed,
  }
}

const AppSuspenseFallback = ({
  allowMobileRoute,
  isAuthFreeRoute,
  isChromeCompanionRoute,
  isInsightsRoute,
  isSessionEntryRoute,
  location,
}) => {
  if (isInsightsRoute) {
    const pathname = isChromeCompanionRoute
      ? location.pathname.replace(/^\/chrome\/insights/, '/insights')
      : location.pathname

    return (
      <InsightsBootstrapFallback
        pathname={pathname}
        search={location.search}
        basePath="/insights"
        isChromeCompanion={isChromeCompanionRoute}
      />
    )
  }

  if (!isAuthFreeRoute && !isSessionEntryRoute) {
    return <AuthenticatedAppFallback hideNavigationOnMobile={allowMobileRoute} />
  }

  return <SessionBootstrapFallback />
}

const PublicAppRoute = ({ Component, loggedIn }) => (
  <div className="global-container">
    <DocumentHead loggedIn={loggedIn} />
    <Suspense fallback={<LoadingShim />}>
      <Component />
    </Suspense>
  </div>
)

const PUBLIC_ROUTE_COMPONENTS = {
  '/brand-hub': BrandHub,
  '/public/changelog': Changelog,
  '/reset-password': PasswordReset,
}

const App = ({ location }) => {
  const queryClient = useQueryClient()
  const auth = useCurrentUser()
  const fetchUser = useCallback(
    (bootstrapUser) => establishCurrentUser(queryClient, bootstrapUser),
    [queryClient],
  )

  const loggedIn = Boolean(auth.id)
  const isAuthFreeRoute = isAuthFreePathname(location.pathname)
  const isSessionEntryRoute = isSessionEntryPathname(location.pathname)
  const isChromeCompanionRoute = isChromeCompanionRequest(location)
  const { companionContext, companionSearch, isFramed } = useChromeCompanionBootstrap({
    isChromeCompanionRoute,
    location,
  })
  const isChromeCompanionInsightsRoute =
    isChromeCompanionRoute && isChromeCompanionInsightsPathname(location.pathname)
  const isInsightsRoute = isInsightsPathname(location.pathname) || isChromeCompanionInsightsRoute
  const allowMobileRoute = authenticatedRouteAllowsMobile(location.pathname)
  // Read at resolve time, so the preload sees where the user is by then.
  const currentLocationRef = useRef(location)
  currentLocationRef.current = location
  const preloadInsightsForUser = useCallback((user) => {
    const { pathname, search } = currentLocationRef.current
    if (shouldPreloadInsights({ pathname, search, user })) {
      void loadCommunityPortfolioOptions(user.id).catch(() => {})
    }
  }, [])
  const { sessionResolved, sessionLoadFailed, retrySession } = useSessionBootstrap({
    fetchUser,
    isAuthFreeRoute,
    loggedIn,
    onUser: preloadInsightsForUser,
  })
  const isWaitingForSession = !isAuthFreeRoute && !sessionResolved
  const routeRedirect = loggedIn && sessionResolved
    ? resolveAuthenticatedRoute({
        companionSearch,
        isFramed,
        pathname: location.pathname,
        search: location.search,
        user: auth,
      })
    : null
  const suspenseFallback = (
    <AppSuspenseFallback
      allowMobileRoute={allowMobileRoute}
      isAuthFreeRoute={isAuthFreeRoute}
      isChromeCompanionRoute={isChromeCompanionRoute}
      isInsightsRoute={isInsightsRoute}
      isSessionEntryRoute={isSessionEntryRoute}
      location={location}
    />
  )
  const shouldSkipCurrentRouteActivity = () => Boolean(routeRedirect)

  useRouteActivityLogger({
    enabled: loggedIn && sessionResolved,
    location,
    shouldSkipRouteActivity: shouldSkipCurrentRouteActivity,
  })

  useEffect(() => {
    if (!isInsightsRoute) endInsightsInitialNavigation()
  }, [isInsightsRoute])

  useEffect(() => {
    // Defer tracking script initialization to avoid blocking render
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => initializeScripts({ isProduction }))
    } else {
      setTimeout(() => initializeScripts({ isProduction }), 1)
    }
  }, [])

  const PublicRouteComponent = location.pathname.startsWith('/maps/')
    ? PublicPropertyMapPage
    : PUBLIC_ROUTE_COMPONENTS[location.pathname]
  if (PublicRouteComponent) {
    return <PublicAppRoute Component={PublicRouteComponent} loggedIn={loggedIn} />
  }

  if (isWaitingForSession) {
    return (
      <div className="global-container">
        <DocumentHead loggedIn={loggedIn} />
        {isInsightsRoute ? <InsightsRoutePreloader /> : null}
        {suspenseFallback}
      </div>
    )
  }

  // The session could not be checked (not a 401), so the user may still be
  // signed in. Offer a retry instead of the login form.
  if (sessionLoadFailed && !loggedIn && !isAuthFreeRoute) {
    return (
      <div className="global-container">
        <DocumentHead loggedIn={false} />
        <SessionLoadError onRetry={retrySession} />
      </div>
    )
  }

  if (routeRedirect) {
    return <Redirect to={routeRedirect} />
  }

  return (
    <div className="global-container">
      <DocumentHead accountName={auth?.isInternal ? auth?.account?.name : undefined} loggedIn={loggedIn} />
      <Suspense fallback={suspenseFallback}>
        <ChromeCompanionContext.Provider value={companionContext}>
          {loggedIn ? <ChromeCompanionSetup user={auth}><AuthHighComponent /></ChromeCompanionSetup> : <Onboarding />}
        </ChromeCompanionContext.Provider>
      </Suspense>
    </div>
  )
}

App.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export default withRouter(App)
